import React, { MouseEvent, useState } from "react";

import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  Maybe,
  MdmDeviceConfigDto,
  MdmDeviceConfigDtoInput,
  MeasurementMappingInput,
  useCreateUpdateDeviceConfigurationMutation,
  useDeleteDeviceConfigurationMutation,
} from "gql/generated/types";
import { GET_PROJECT } from "gql/project.operations";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { confirmPopup } from "primereact/confirmpopup";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { convertTimeSpanToString, secondsToTimeSpan } from "utils/time-utils";
import { apolloErrorToaster, successToaster } from "../utils/utils";

type DeviceConfigurationProps = {
  configurations:  Maybe<Maybe<MdmDeviceConfigDto>[]> | undefined
}

const DeviceConfiguration: React.FC<DeviceConfigurationProps> = ({ configurations }) => {
  const [title, setTitle] = useState("");
  const { projectId } = useParams();
  const [createDeviceConfiguration] = useCreateUpdateDeviceConfigurationMutation();

  const mappedConfigs = configurations?.map(config => (
    <MeasurementMapping key={config?.id} config={config} />
  ));

  const handleCreateDeviceConfiguration = () => {
      createDeviceConfiguration({
        variables: { input: { title, projectId, measurementMappings: [] } },
        update: (cache, { data }) => {
          cache.updateQuery({ query: GET_PROJECT, variables: { id: projectId } }, oldData => ({
            mdmProjectById: {
              ...oldData.mdmProjectById,
              deviceConfigs: [
                ...oldData.mdmProjectById.deviceConfigs,
                data?.createUpdateDeviceConfig,
              ],
            },
          }));
        }
      });
      setTitle("");
  }

  return (
    <div>
      {configurations?.length! > 0 && mappedConfigs}
      <div className="flex mt-3">
        <div className="p-inputgroup w-3">
          <InputText
            value={title}
            onChange={e => setTitle(e.target.value)}
            name="title"
            placeholder={T("word.title-for-configuration")}
            className="w-15rem"
          />
          <Button
            type="button"
            icon="pi pi-plus"
            className="p-inputgroup-addon "
            title={T("action.add-new-configuration")}
            onClick={handleCreateDeviceConfiguration}
          />
        </div>
      </div>
    </div>
  );
}

export default DeviceConfiguration;

type MeasurementMappingProps = {
  config: Maybe<MdmDeviceConfigDto> | undefined
}

const MeasurementMapping: React.FC<MeasurementMappingProps> = ({config}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteDeviceConfig] = useDeleteDeviceConfigurationMutation({
    onCompleted: () => successToaster(T("alert.device-configuration-deleted-successfully")),
    onError: apolloErrorToaster,
  });

  const handleDeleteDeviceConfig = (event: MouseEvent<HTMLButtonElement>) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-dialog.are-you-sure-you-want-to-delete"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        if (config) {
          deleteDeviceConfig({
            variables: {id: config?.id!},
            update(cache) {
              const normalizedId = cache.identify({ id: config.id, __typename: "MdmDeviceConfigDTO" });
              cache.evict({ id: normalizedId });
              cache.gc();
            },
          })
        }
      },
    });
  }

  return (
    <div className="grid border-1 border-300 border-round p-3 mb-3 mdm-device">
      <InputLabelWrapper
        labelClassName="font-bold"
        labelName={T("word.title")}
        className="gap-3 border-bottom-1 md:border-bottom-none md:border-right-1 border-200"
        col={3}
      >
        <p>{config?.title}</p>
      </InputLabelWrapper>
      <InputLabelWrapper
        labelClassName="font-bold"
        labelName={T("mdm.vitality-threshold")}
        className="gap-3 border-bottom-1 md:border-bottom-none md:border-right-1 border-200"
        col={2}
      >
        <p>{convertTimeSpanToString(secondsToTimeSpan(config?.vitalityThreshold ?? 0))}</p>
      </InputLabelWrapper>
      <InputLabelWrapper
        labelClassName="font-bold"
        labelName={T("mdm.measurement-mappings")}
        className="gap-3 border-bottom-1 md:border-bottom-none md:border-right-1 border-200"
        col={5}
      >
        {/*  table alternating row colors */}
        <table className="tag-table">
            <thead>
                <tr>
                    <th>{T("mdm.mapping")}</th>
                    <th>{T("word.tags")}</th>
                </tr>
            </thead>
            <tbody>
                {config?.measurementMappings.map((mapper, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={i}>
                        <td>{mapper?.key}</td>
                        <td>
                        {mapper?.value.map(chip => (
                            <Tag key={chip} value={chip} className="mx-1" title={chip ?? ""} />
                        ))}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
      </InputLabelWrapper>
      <InputLabelWrapper labelClassName="font-bold" labelName={T("word.actions")} className="gap-3" col={2}>
        <div className="flex flex-nowrap gap-1">
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text p-button-raised"
            onClick={() => setIsOpen(true)}
          />
          <Button
            id={config?.id!}
            icon="pi pi-trash"
            className="p-button-rounded p-button-text p-button-danger p-button-raised"
            onClick={handleDeleteDeviceConfig}
          />
        </div>
      </InputLabelWrapper>
      <Dialog
        visible={isOpen}
        onHide={() => setIsOpen(false)}
        header={T("mdm.edit-configuration")}
        style={{ width: "700px" }}
      >
        <DeviceConfigurationForm configuration={config} closeDialog={() => setIsOpen(false)} />
      </Dialog>
    </div>
  );
}

type DeviceConfigurationFormProps = {
  configuration: Maybe<MdmDeviceConfigDto> | undefined
  closeDialog: () => void
}

const DeviceConfigurationForm: React.FC<DeviceConfigurationFormProps> = ({ configuration, closeDialog }) => {
  const [config, setConfig] = useState(configuration);
  const { projectId } = useParams();
  const [updateDeviceConfiguration] = useCreateUpdateDeviceConfigurationMutation({
    onCompleted: () => {
      easasAlert(T("alert.device-configuration-updated-successfully"));
      closeDialog();
    },
    onError: apolloErrorToaster,
  });

  const handleUpdateDeviceConfig = () => {
    updateDeviceConfiguration({
      variables: { input: { ...config, projectId } as MdmDeviceConfigDtoInput },
    });
  }
  
  return (
    <div className="grid align-items-end">
      <InputLabelWrapper labelName={T("word.title")} className="gap-3">
        <InputText
          value={config?.title ?? ""}
          onChange={e => setConfig({ ...config!, title: e.target.value })}
          name="title"
          placeholder={T("word.title-for-configuration")}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("mdm.vitality-threshold")} className="gap-3">
        <div className="p-inputgroup">
          <InputNumber
            value={config?.vitalityThreshold}
            onChange={e => setConfig({ ...config!, vitalityThreshold: e.value })}
            name="vitalityThreshold"
            placeholder={T("time.seconds")}
            suffix=" s"
          />
          <span className="p-inputgroup-addon" title={T("word.seconds-to-time")}>
            <span className="text-2xl mx-2 h-1rem" style={{ marginTop: "-10px" }}>&#8658;</span>
            <span className="text-sm">
              {convertTimeSpanToString(secondsToTimeSpan(config?.vitalityThreshold ?? 0))}
            </span>
          </span>
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("mdm.measurement-mappings")} className="gap-3" col={12}>
        {config?.measurementMappings.map((mapper, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="flex flex-row gap-3">
            <InputText
              value={mapper?.key ?? ""}
              onChange={e =>
                setConfig(prev => ({
                  ...prev,
                  measurementMappings: prev?.measurementMappings?.map((it, ind) =>
                    ind === i ? { ...it, key: e.target.value } : it
                  ) as Maybe<MeasurementMappingInput>[],
                }))
              }
              name="key"
              placeholder={T("word.key")}
            />
            <Chips
              value={(mapper?.value as string[]) ?? []}
              onChange={e =>
                setConfig(prev => ({
                  ...prev,
                  measurementMappings: prev?.measurementMappings?.map(it =>
                    it?.key === mapper?.key ? { ...it, value: e.value } : it
                  ) as Maybe<MeasurementMappingInput>[],
                }))
              }
              name="value"
              placeholder={T("word.tags")}
            />
            <Button
              title={T("action.delete-measurement")}
              icon="pi pi-trash"
              className="p-button-rounded p-button-text p-button-raised p-button-danger"
              onClick={() =>
                setConfig(prev => ({
                  ...prev,
                  measurementMappings: prev?.measurementMappings.toSpliced(
                    i,
                    1
                  ) as Maybe<MeasurementMappingInput>[],
                }))
              }
            />
          </div>
        ))}
      </InputLabelWrapper>
      <div className="flex justify-content-between w-full mt-3 mx-4">
        <Button
          title={T("action.add-measurement")}
          icon="pi pi-plus"
          className="p-button-rounded p-button-outlined p-button-raised p-button-success"
          onClick={() =>
            setConfig({
              ...config,
              measurementMappings: [...(config?.measurementMappings ?? []), { key: "", value: [] }],
            })
          }
        />
        <Button
          label={T("action.update")}
          title="Save configuration"
          className="p-button-rounded "
          onClick={handleUpdateDeviceConfig}
        />
      </div>
    </div>
  );
}